import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { usePermissions } from '@/common/hooks/usePermissions';
import { teamsService, Team } from '@/features/teams/services/teams.service';
import { TeamFormDialog } from '@/features/teams/components/TeamFormDialog';
import { TeamUsersDialog } from '@/features/teams/components/TeamUsersDialog';

export const Route = createFileRoute('/_authenticated/teams')({
  component: TeamsPage,
});

function TeamsPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [formOpen, setFormOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  const canManage = hasPermission('teams.manage');

  // Buscar equipes
  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const response = await teamsService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canManage,
  });

  const teams: Team[] = teamsData || [];

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => teamsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const handleCreate = () => {
    setSelectedTeam(null);
    setFormOpen(true);
  };

  const handleEdit = (team: Team) => {
    setSelectedTeam(team);
    setFormOpen(true);
  };

  const handleDelete = async (team: Team) => {
    const ok = await confirm({
      title: `Excluir a equipe "${team.name}"?`,
      description: 'Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir equipe',
      destructive: true,
    });
    if (ok) deleteMutation.mutate(team.id);
  };

  const handleManageUsers = (team: Team) => {
    setSelectedTeam(team);
    setUsersDialogOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['teams'] });
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Você não tem permissão para acessar esta página.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Equipes"
        description="Gerencie as equipes do sistema"
        actions={
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nova equipe
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Lista de Equipes</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">Carregando...</p>
          ) : teams.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma equipe cadastrada
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Usuários</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teams.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell className="font-medium">{team.name}</TableCell>
                    <TableCell>{team.description || '-'}</TableCell>
                    <TableCell>{team.userCount || 0}</TableCell>
                    <TableCell>
                      {format(new Date(team.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleManageUsers(team)}
                          aria-label={`Gerenciar usuários de ${team.name}`}
                          title="Gerenciar usuários"
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(team)}
                          aria-label={`Editar ${team.name}`}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(team)}
                          disabled={deleteMutation.isPending}
                          aria-label={`Excluir ${team.name}`}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TeamFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={handleSuccess}
        team={selectedTeam || undefined}
      />

      <TeamUsersDialog
        open={usersDialogOpen}
        onOpenChange={setUsersDialogOpen}
        team={selectedTeam}
      />
    </div>
  );
}

